import { Component, Inject, OnInit } from '@angular/core';
import { Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import { tap } from 'rxjs';
import { fullName } from 'src/app/shared/functions/full-name';
import { markControlsAsTouched } from 'src/app/shared/functions/mark-controls-as-touched';
import { IOptionGroup } from 'src/app/shared/models/option-group.model';
import { noEmptyTrimmedStringValidator } from 'src/app/shared/validators/no-empty-string-validator';
import { NonConformanceActionType } from '../../enums/non-conformance-action-type.enum';
import { CommentForm } from '../../forms/comment.form';
import { IdComment, INotificationRecipientSelection } from '../../models/id-comment.model';
import { INotificationRecipientsResponse } from '../../models/notification-recipients.model';
import { LoadNotificationRecipients } from '../../state/non-conformance.actions';
import { NonConformanceState } from '../../state/non-conformance.state';

export enum NotificationRecipientType {
  Position = 'positions',
  PositionGroup = 'jobPositionGroups',
  Role = 'roles',
  User = 'users',
}

export interface ICommentDialogData {
  id: string;
  title: string;
  text: string;
  confirmButtonText: string;
  isOptional: boolean;
  actionType?: NonConformanceActionType;
  departmentId?: string;
}

@Component({
  selector: 'app-comment-dialog',
  templateUrl: './comment-dialog.component.html',
  styleUrls: ['./comment-dialog.component.scss'],
  standalone: false,
})
export class CommentDialogComponent implements OnInit {
  form: CommentForm;
  notificationOptionGroups: IOptionGroup[] = [];
  hasNotificationRecipients = false;
  loadingActions = [LoadNotificationRecipients];

  constructor(
    public dialogRef: MatDialogRef<CommentDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: ICommentDialogData,
    private store: Store
  ) {}

  ngOnInit(): void {
    this.dialogRef.updateSize('30rem');
    this.form = new CommentForm(this.data.id);
    if (!this.data.isOptional) {
      this.form.comment.setValidators([Validators.required, noEmptyTrimmedStringValidator]);
    } else {
      this.form.comment.clearValidators();
    }

    this.loadNotificationRecipients();
  }

  private loadNotificationRecipients(): void {
    if (!this.data.actionType) {
      return;
    }

    this.store
      .dispatch(
        new LoadNotificationRecipients(this.data.id, this.data.actionType, this.data.departmentId)
      )
      .pipe(
        tap(() => {
          const recipients = this.store.selectSnapshot(
            NonConformanceState.getNotificationRecipients
          );
          this.initNotificationRecipients(recipients);
        })
      )
      .subscribe();
  }

  private initNotificationRecipients(recipients?: INotificationRecipientsResponse): void {
    if (!recipients) {
      return;
    }

    this.notificationOptionGroups = [];

    if (recipients.users?.length > 0) {
      this.notificationOptionGroups.push({
        type: NotificationRecipientType.User,
        options: recipients.users.map((user) => ({
          id: user.id,
          name: fullName(user),
        })),
      });
    }

    if (recipients.positions?.length > 0) {
      this.notificationOptionGroups.push({
        type: NotificationRecipientType.Position,
        options: recipients.positions,
      });
    }

    if (recipients.positionGroups?.length > 0) {
      this.notificationOptionGroups.push({
        type: NotificationRecipientType.PositionGroup,
        options: recipients.positionGroups,
      });
    }

    if (recipients.roles?.length > 0) {
      this.notificationOptionGroups.push({
        type: NotificationRecipientType.Role,
        options: recipients.roles,
      });
    }

    this.hasNotificationRecipients = this.notificationOptionGroups.length > 0;
    this.selectAllRecipients();
  }

  private selectAllRecipients(): void {
    const allRecipients: INotificationRecipientSelection[] = this.notificationOptionGroups.flatMap(
      (group) =>
        group.options.map((option) => ({
          value: { id: option.id, name: option.name },
          type: group.type,
        }))
    );
    this.form.customNotificationRecipients.setValue(allRecipients);
  }

  save(): void {
    markControlsAsTouched(this.form);

    if (!this.form.valid) {
      return;
    }

    const selectedRecipients: INotificationRecipientSelection[] =
      this.form.customNotificationRecipients.value || [];

    const result: IdComment = {
      id: this.form.id.value,
      comment: this.form.comment.value,
      customNotificationRecipients: this.form.useCustomRecipients.value
        ? {
            useCustomRecipients: this.form.useCustomRecipients.value,
            userIds: this.extractIdsByType(selectedRecipients, NotificationRecipientType.User),
            positionIds: this.extractIdsByType(
              selectedRecipients,
              NotificationRecipientType.Position
            ),
            positionGroupIds: this.extractIdsByType(
              selectedRecipients,
              NotificationRecipientType.PositionGroup
            ),
            roleIds: this.extractIdsByType(selectedRecipients, NotificationRecipientType.Role),
          }
        : { useCustomRecipients: false },
    };
    this.dialogRef.close(result);
  }

  private extractIdsByType(
    selections: INotificationRecipientSelection[],
    type: NotificationRecipientType
  ): string[] {
    return selections.filter((s) => s.type === type).map((s) => s.value.id);
  }
}
